import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Stack,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  Snackbar,
  TableSortLabel,
  ListItemText,
} from '@mui/material';
import {
  Delete,
  Refresh,
  CloudUpload,
  Search,
  FilterList,
  GetApp,
  Visibility,
} from '@mui/icons-material';

import { API_BASE } from '../utils/apiUtils';

const DocumentManagement = () => {
  // Upload states
  const [file, setFile] = useState(null);
  const [organizationId, setOrganizationId] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({ displayName: '', file: '', departments: '' });

  // Document list states
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('uploadedAt'); // Changed from createdAt
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // UI states
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({});

  // Constants
  
  // No longer need Basic Auth headers - using session authentication

  // Get user's organization from localStorage and fetch departments
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    console.log('User data from localStorage:', userData);
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log('Parsed user data:', parsedUser);
        const userOrgId = parsedUser.organization?.id;
        console.log('User organization ID:', userOrgId);
        if (userOrgId) {
          setOrganizationId(userOrgId);
          fetchDepartments(); // Fetch departments based on user permissions
        } else {
          console.error('No organization ID found in user data');
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    } else {
      console.error('No user data found in localStorage');
    }
  }, []);

  // Fetch departments based on user permissions
  const fetchDepartments = async () => {
    try {
      console.log('Fetching departments based on user permissions');
      const res = await fetch(`${API_BASE}/documents/departments`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        console.log('Departments fetched:', data);
        setDepartments(data);
      } else {
        console.error('Failed to fetch departments, status:', res.status);
        const errorText = await res.text();
        console.error('Error response:', errorText);
        showSnackbar('Failed to fetch departments', 'error');
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      showSnackbar('Error fetching departments', 'error');
    }
  };

  // Memoized fetch functions to prevent unnecessary re-renders
  const fetchDocuments = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        organizationId,
        page: page.toString(),
        size: pageSize.toString(),
        sortBy,
        sortDir,
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      console.log('Fetching documents with params:', params.toString());

      const res = await fetch(`${API_BASE}/documents/list?${params}`, {
        credentials: 'include',

      });

      if (res.ok) {
        const data = await res.json();
        console.log('API Response:', data);

        // Handle both paginated and non-paginated responses
        if (Array.isArray(data)) {
          // Non-paginated response
          setDocuments(data);
          setTotalPages(1);
          setTotalElements(data.length);
        } else {
          // Paginated response
        setDocuments(data.content || []);
          setTotalPages(data.totalPages || 1);
        setTotalElements(data.totalElements || 0);
      }
      } else {
        console.error('Failed to fetch documents, status:', res.status);
        showSnackbar('Failed to fetch documents', 'error');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      showSnackbar('Error fetching documents', 'error');
    } finally {
      setLoading(false);
    }
  }, [organizationId, page, pageSize, sortBy, sortDir, searchTerm, statusFilter]);

  const fetchStats = useCallback(async () => {
    if (!organizationId) return;

    try {
      const res = await fetch(`${API_BASE}/documents/stats?organizationId=${organizationId}`, {
        credentials: 'include',

      });

      if (res.ok) {
        const data = await res.json();
        console.log('Stats Response:', data);
        setStats(data);
      } else {
        console.error('Failed to fetch stats, status:', res.status);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [organizationId]);

  // Fetch documents and stats when dependencies change
  useEffect(() => {
    if (organizationId) {
      fetchDocuments();
      fetchStats();
    }
  }, [organizationId, fetchDocuments, fetchStats]);

  // Separate effect for search and filter changes with debouncing
  useEffect(() => {
    if (!organizationId) return;

    const timeoutId = setTimeout(() => {
      setPage(0); // Reset to first page when searching/filtering
      fetchDocuments();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter]);

  // No longer needed - user's organization is fixed

  const handleFileUpload = async (e) => {
    e && e.preventDefault && e.preventDefault();

    // Client-side validation
    const errs = { displayName: '', file: '', departments: '' };
    if (!displayName || !displayName.trim()) errs.displayName = 'Display name is required';
    if (!file) errs.file = 'Please choose a file';
    if (selectedDepartments.length === 0) errs.departments = 'Select at least one department';
    setUploadErrors(errs);
    if (errs.displayName || errs.file || errs.departments) {
      setUploadStatus('error');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('organizationId', organizationId);
    formData.append('departments', selectedDepartments.join(','));
    formData.append('displayName', displayName.trim());

    try {
      // All files go to document upload (PDF, Excel, JSON, etc.)
      const response = await fetch(`${API_BASE}/documents/upload`, {
        credentials: 'include',
        method: 'POST',

        body: formData,
      });

      if (response.ok) {
        setUploadStatus('success');
        setFile(null);
        setDisplayName('');
        setSelectedDepartments([]);
        setUploadDialogOpen(false);
        showSnackbar('Document uploaded successfully!', 'success');
        // Refresh data
        fetchDocuments();
        fetchStats();
      } else {
        setUploadStatus('error');
        const errorText = await response.text();
        showSnackbar(errorText || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      showSnackbar('Upload failed', 'error');
    }
  };


  const handleDelete = async (documentId) => {
    try {
      const response = await fetch(`${API_BASE}/documents/delete/${documentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        showSnackbar('Document deleted successfully!', 'success');
        fetchDocuments();
        fetchStats();
      } else {
        const errorText = await response.text();
        showSnackbar(errorText || 'Delete failed', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showSnackbar('Delete failed', 'error');
    }
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleBatchDelete = async () => {
    if (selectedDocuments.length === 0) return;

    try {
      const response = await fetch(`${API_BASE}/documents/batch`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedDocuments),
      });

      if (response.ok) {
        const result = await response.text();
        showSnackbar(result, 'success');
        setSelectedDocuments([]);
        fetchDocuments();
        fetchStats();
      } else {
        const errorText = await response.text();
        showSnackbar(errorText || 'Batch delete failed', 'error');
      }
    } catch (error) {
      console.error('Batch delete error:', error);
      showSnackbar('Batch delete failed', 'error');
    }
  };

  const handleReprocess = async (documentId) => {
    try {
      const response = await fetch(`${API_BASE}/documents/${documentId}/reprocess`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        showSnackbar('Document reprocessing started!', 'info');
        fetchDocuments();
        fetchStats();
      } else {
        const errorText = await response.text();
        showSnackbar(errorText || 'Reprocess failed', 'error');
      }
    } catch (error) {
      console.error('Reprocess error:', error);
      showSnackbar('Reprocess failed', 'error');
    }
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortDir === 'asc';
    setSortBy(property);
    setSortDir(isAsc ? 'desc' : 'asc');
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = documents.map(doc => doc.id);
      setSelectedDocuments(newSelected);
    } else {
      setSelectedDocuments([]);
    }
  };

  const handleSelectDocument = (documentId) => {
    const selectedIndex = selectedDocuments.indexOf(documentId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedDocuments, documentId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedDocuments.slice(1));
    } else if (selectedIndex === selectedDocuments.length - 1) {
      newSelected = newSelected.concat(selectedDocuments.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedDocuments.slice(0, selectedIndex),
        selectedDocuments.slice(selectedIndex + 1),
      );
    }

    setSelectedDocuments(newSelected);
  };

  const handlePageChange = (event, value) => {
    setPage(value - 1); // Convert to 0-based indexing
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(0); // Reset to first page
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PROCESSING': return 'warning';
      case 'FAILED': return 'error';
      case 'PENDING': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const dt = new Date(dateString);
    if (isNaN(dt.getTime())) return '-';
    return dt.toLocaleString();
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 3 }}>
        <Typography variant="h4" gutterBottom>
          Document Management
        </Typography>

        {/* Upload Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Documents</Typography>
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload Document
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Stats Section */}
        {organizationId && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">{stats.totalDocuments || 0}</Typography>
                  <Typography variant="body2">Total</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">
                    {stats.completedDocuments || 0}
                  </Typography>
                  <Typography variant="body2">Completed</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="warning.main">
                    {stats.processingDocuments || 0}
                  </Typography>
                  <Typography variant="body2">Processing</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="info.main">
                    {stats.pendingDocuments || 0}
                  </Typography>
                  <Typography variant="body2">Pending</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="error.main">
                    {stats.failedDocuments || 0}
                  </Typography>
                  <Typography variant="body2">Failed</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters and Actions */}
        {organizationId && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Search documents"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status Filter</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status Filter"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="PROCESSING">Processing</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="FAILED">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Page Size</InputLabel>
                    <Select
                      value={pageSize}
                      label="Page Size"
                      onChange={handlePageSizeChange}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        fetchDocuments();
                        fetchStats();
                      }}
                      startIcon={<Refresh />}
                      size="small"
                    >
                      Refresh
                    </Button>
                    {selectedDocuments.length > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleBatchDelete}
                        startIcon={<Delete />}
                        size="small"
                      >
                        Delete ({selectedDocuments.length})
                      </Button>
                    )}
          </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Documents Table */}
        {organizationId && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < documents.length}
                      checked={documents.length > 0 && selectedDocuments.length === documents.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'filename'}
                      direction={sortBy === 'filename' ? sortDir : 'asc'}
                      onClick={() => handleSort('filename')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Departments</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDir : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'uploadedAt'}
                      direction={sortBy === 'uploadedAt' ? sortDir : 'asc'}
                      onClick={() => handleSort('uploadedAt')}
                    >
                      Uploaded
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Processed</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      No documents found
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDocuments.indexOf(doc.id) !== -1}
                          onChange={() => handleSelectDocument(doc.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={doc.filename}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                            {doc.displayName && doc.displayName.trim() ? doc.displayName : doc.filename}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {Array.isArray(doc.departments) && doc.departments.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 300 }}>
                            {doc.departments.map((d) => (
                              <Chip key={d} label={departments.find(x => x.code === d)?.name || d} size="small" />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={doc.status}
                          color={getStatusColor(doc.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(doc.uploadedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(doc.processedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Reprocess">
                            <IconButton
                              size="small"
                              onClick={() => handleReprocess(doc.id)}
                              disabled={doc.status === 'PROCESSING'}
                            >
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDocumentToDelete(doc);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {organizationId && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
        </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{documentToDelete?.filename}"?
              This will remove the document and all its embeddings permanently.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => handleDelete(documentToDelete?.id)}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Upload file</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                error={!!uploadErrors.displayName}
                helperText={uploadErrors.displayName}
              />

              <FormControl fullWidth required error={!!uploadErrors.departments}>
                <InputLabel>Departments</InputLabel>
                <Select
                  multiple
                  value={selectedDepartments}
                  label="Departments"
                  onChange={(e) => setSelectedDepartments(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={departments.find(d => d.code === value)?.name || value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.code}>
                      <Checkbox checked={selectedDepartments.indexOf(dept.code) > -1} />
                      <ListItemText primary={dept.name} secondary={dept.code} />
                    </MenuItem>
                  ))}
                </Select>
                {uploadErrors.departments && (
                  <Typography variant="caption" color="error">{uploadErrors.departments}</Typography>
                )}
              </FormControl>

              <div>
                <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                  Choose File
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                </Button>
                {file && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {file.name}
                  </Typography>
                )}
                {uploadErrors.file && (
                  <Typography variant="caption" color="error">{uploadErrors.file}</Typography>
                )}
              </div>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleFileUpload}>Upload</Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
};


export default DocumentManagement;